/**
 * Detection helpers for the Bhookly Server Android WebView shell.
 * The native app injects `window.BhooklyNative` with printJobs().
 *
 * Android WebView quirks:
 * - Interface methods often fail `typeof === 'function'` checks
 * - Reading undeclared properties on the bridge can throw
 * Prefer `window.BhooklyNativeApp` (set by the shell) + callable printJobs.
 */

export interface BhooklyNativeBridge {
  isNativeServerApp?: boolean
  getIsNativeServerApp?: () => boolean
  printJobs?: (jobsJson: string) => void
  getTenantHost?: () => string
  /** Push receipt logo + business name into the Android chrome / task icon. */
  setBranding?: (logoUrl: string, businessName: string) => void
  /**
   * The APK's versionName, e.g. "1.4.0". Added in agent 1.4.0 (F-AND-N1).
   *
   * Absent on every older agent, which is exactly what makes it useful: it is
   * how we tell whether a print result is coming back at all. The APK does not
   * auto-install, so un-updated tablets stay in the field indefinitely and
   * nothing may ever WAIT on a callback without checking this first.
   */
  getAgentVersion?: () => string
  /** Delivery riders (F-DLV-N3, agent 1.5.0): background location service. */
  startRiderTracking?: (apiBase: string, token: string) => boolean
  stopRiderTracking?: () => void
  getRiderTrackingState?: () => string
  requestRiderLocationPermission?: () => void
}

declare global {
  interface Window {
    BhooklyNative?: BhooklyNativeBridge
    BhooklyNativeApp?: boolean
  }
}

/** True when running inside the Bhookly Server Android WebView. */
export function isBhooklyNativeServerApp(): boolean {
  if (typeof window === 'undefined') return false
  // Shell marker — most reliable (set on every page load / SPA nav).
  if (window.BhooklyNativeApp === true) return true

  const n = window.BhooklyNative
  if (!n) return false

  try {
    if (n.isNativeServerApp === true) return true
  } catch {
    /* Android may throw on property get */
  }
  try {
    if (n.getIsNativeServerApp?.()) return true
  } catch {
    /* bridge not ready */
  }
  // Any injectable print entrypoint means we're in the shell.
  try {
    if (n.printJobs != null) return true
  } catch {
    /* ignore */
  }
  return false
}

/** Immersive server tablet mode (?embedded=server or native shell). */
export function isEmbeddedServerMode(): boolean {
  if (typeof window === 'undefined') return false
  if (isBhooklyNativeServerApp()) return true
  try {
    return new URLSearchParams(window.location.search).get('embedded') === 'server'
  } catch {
    return false
  }
}

export function nativePrintJobs(jobsJson: string): boolean {
  const n = window.BhooklyNative
  if (!n) return false
  try {
    // Do NOT use typeof === 'function' — Android JavascriptInterface methods
    // often fail that check even when callable.
    if (n.printJobs == null) return false
    n.printJobs(jobsJson)
    return true
  } catch {
    return false
  }
}

/**
 * The Android agent's version string, or '' when it predates 1.4.0 / we are not
 * in the shell. Every access is guarded: Android WebView throws on reading an
 * undeclared property of a JavascriptInterface, so a plain `?.` is not enough.
 */
export function nativeAgentVersion(): string {
  if (typeof window === 'undefined') return ''
  const n = window.BhooklyNative
  if (!n) return ''
  try {
    if (n.getAgentVersion == null) return ''
    return (n.getAgentVersion() || '').trim()
  } catch {
    return ''
  }
}

export function nativeSetBranding(logoUrl: string, businessName: string): void {
  if (!isBhooklyNativeServerApp()) return
  try {
    window.BhooklyNative?.setBranding?.(logoUrl || '', businessName || '')
  } catch {
    /* ignore */
  }
}

/** Compare dotted versions numerically. Missing segments count as 0. */
export function versionAtLeast(actual: string, minimum: string): boolean {
  if (!actual) return false
  const a = actual.split('.').map((p) => parseInt(p, 10))
  const b = minimum.split('.').map((p) => parseInt(p, 10))
  const len = Math.max(a.length, b.length)
  for (let i = 0; i < len; i += 1) {
    const x = Number.isFinite(a[i]) ? a[i] : 0
    const y = Number.isFinite(b[i]) ? b[i] : 0
    if (x > y) return true
    if (x < y) return false
  }
  return true
}

// ── Delivery riders (F-DLV-N3) ──────────────────────────────────────────────

/** First agent with the background location service. */
export const RIDER_TRACKING_AGENT = '1.5.0'

export interface NativeRiderTrackingState {
  running: boolean
  /** Location permission granted. */
  fine: boolean
  /** "Allow all the time" granted — tracking survives the screen going off. */
  background: boolean
  /** epoch ms of the last batch the service delivered, 0 = never. */
  last_sent_at: number
}

/** True inside the Android app at a version that can track in the background. */
export function nativeRiderTrackingSupported(): boolean {
  if (!isBhooklyNativeServerApp()) return false
  const v = nativeAgentVersion()
  return v !== '' && versionAtLeast(v, RIDER_TRACKING_AGENT)
}

export function nativeStartRiderTracking(apiBase: string, token: string): boolean {
  try {
    return window.BhooklyNative?.startRiderTracking?.(apiBase, token) === true
  } catch {
    return false
  }
}

export function nativeStopRiderTracking(): void {
  try {
    window.BhooklyNative?.stopRiderTracking?.()
  } catch {
    /* ignore */
  }
}

export function nativeRequestRiderLocationPermission(): void {
  try {
    window.BhooklyNative?.requestRiderLocationPermission?.()
  } catch {
    /* ignore */
  }
}

export function nativeRiderTrackingState(): NativeRiderTrackingState | null {
  try {
    const raw = window.BhooklyNative?.getRiderTrackingState?.()
    if (!raw) return null
    const s = JSON.parse(raw) as Partial<NativeRiderTrackingState>
    return {
      running: s.running === true,
      fine: s.fine === true,
      background: s.background === true,
      last_sent_at: typeof s.last_sent_at === 'number' ? s.last_sent_at : 0,
    }
  } catch {
    return null
  }
}
